/**
 * Arabic text normalization for MATCHING and SEARCH (keyword triggers, opt-out keywords,
 * inbox/contact search). Never use the output for display — it deliberately loses
 * information (diacritics, letter variants, case).
 *
 * Canonical form, applied in this order (each step documented in README.md):
 *  1. Unicode NFKC — folds Arabic presentation forms and ligatures (lam-alef ligature →
 *     لا, Allah ligature → الله), full-width Latin, and composes decomposed hamza/madda
 *     sequences (alef + combining madda → آ).
 *  2. Remove invisible characters: tatweel/kashida (U+0640), bidi controls (LRM/RLM/ALM,
 *     embeddings, isolates), zero-width space/non-joiner, BOM, soft hyphen; a zero-width
 *     joiner is removed only next to Arabic letters (emoji ZWJ sequences survive).
 *  3. Remove Arabic combining marks: tashkeel (fatha, damma, kasra, sukun, shadda,
 *     tanween), dagger alef, hamza/madda marks, Quranic annotation marks.
 *  4. Fold letter variants: أ إ آ ٱ ٲ ٳ → ا · ى → ي · Persian/Urdu ی → ي, ک → ك, ھ → ه ·
 *     ة → ه (option `foldTehMarbuta`, on by default) · ؤ → و, ئ → ي (option
 *     `foldHamzaCarriers`, off by default).
 *  5. Digits and punctuation: Arabic-Indic (٠-٩) and Persian (۰-۹) digits → 0-9;
 *     ٫ → "." · ٬ → "," · ، → "," · ؛ → ";" · ؟ → "?" · ٪ → "%" · ۔ → ".".
 *  6. Lower-case Latin letters (option `caseFold`, on by default). Arabic has no case.
 *  7. Re-apply NFKC (removals can leave composable Latin pairs), then collapse every run
 *     of whitespace (incl. NBSP, thin spaces, line breaks) to one ASCII space and trim.
 *
 * The function is idempotent: `normalizeArabic(normalizeArabic(x)) === normalizeArabic(x)`.
 * Non-Arabic text (Latin, emoji, URLs, numbers) passes through unchanged apart from
 * NFKC, case folding and whitespace collapsing, so mixed Arabic/English input is safe.
 *
 * Code points are written as `\u{…}` escapes on purpose: invisible characters must never
 * appear literally in source.
 */
export interface ArabicNormalizationOptions {
  /**
   * Fold teh marbuta to heh (ة → ه). Default `true`: users type «مدرسه» and «مدرسة»
   * interchangeably in chat, and false collisions between the two are rare. Disable for
   * exact-spelling use cases.
   */
  readonly foldTehMarbuta?: boolean;
  /**
   * Fold hamza carriers (ؤ → و, ئ → ي). Default `false`: these distinguish words more
   * often than they are typos, so folding would add false keyword matches.
   */
  readonly foldHamzaCarriers?: boolean;
  /** Lower-case Latin text. Default `true`. */
  readonly caseFold?: boolean;
}

// Step 2 — invisible characters (tatweel U+0640 included).
const INVISIBLE =
  /[\u{0640}\u{00AD}\u{061C}\u{180E}\u{200B}\u{200C}\u{200E}\u{200F}\u{202A}-\u{202E}\u{2060}-\u{2064}\u{2066}-\u{2069}\u{FEFF}]/gu;
// Runs of ZWJ touching an Arabic character (whole runs, so a second pass finds nothing).
const ARABIC_ZWJ = /(?<=\p{Script=Arabic})\u{200D}+|\u{200D}+(?=\p{Script=Arabic})/gu;

// Step 3 — Arabic combining marks (Mn in the Arabic blocks) + high hamza U+0674.
// The class deliberately lists standalone combining marks, each removed individually.
const ARABIC_MARKS =
  // eslint-disable-next-line no-misleading-character-class
  /[\u{0610}-\u{061A}\u{064B}-\u{065F}\u{0670}\u{0674}\u{06D6}-\u{06DC}\u{06DF}-\u{06E4}\u{06E7}\u{06E8}\u{06EA}-\u{06ED}\u{0898}-\u{089F}\u{08CA}-\u{08E1}\u{08E3}-\u{08FF}]/gu;

const ALEF = "\u{0627}";
const YEH = "\u{064A}";
const LETTER_FOLDS: Readonly<Record<string, string>> = {
  "\u{0623}": ALEF, // alef with hamza above
  "\u{0625}": ALEF, // alef with hamza below
  "\u{0622}": ALEF, // alef with madda
  "\u{0671}": ALEF, // alef wasla
  "\u{0672}": ALEF, // alef with wavy hamza above
  "\u{0673}": ALEF, // alef with wavy hamza below
  "\u{0649}": YEH, // alef maqsura
  "\u{06CC}": YEH, // Farsi yeh
  "\u{06A9}": "\u{0643}", // keheh → kaf
  "\u{06BE}": "\u{0647}", // heh doachashmee → heh
};
const LETTER_FOLD_RE =
  /[\u{0622}\u{0623}\u{0625}\u{0649}\u{0671}-\u{0673}\u{06A9}\u{06BE}\u{06CC}]/gu;
const TEH_MARBUTA_RE = /\u{0629}/gu;
const WAW_HAMZA_RE = /\u{0624}/gu;
const YEH_HAMZA_RE = /\u{0626}/gu;

const PUNCTUATION: Readonly<Record<string, string>> = {
  "\u{066B}": ".", // Arabic decimal separator
  "\u{066C}": ",", // Arabic thousands separator
  "\u{060C}": ",", // Arabic comma
  "\u{061B}": ";", // Arabic semicolon
  "\u{061F}": "?", // Arabic question mark
  "\u{066A}": "%", // Arabic percent sign
  "\u{06D4}": ".", // Arabic full stop
};
const DIGIT_PUNCT_RE =
  /[\u{0660}-\u{0669}\u{06F0}-\u{06F9}\u{060C}\u{061B}\u{061F}\u{066A}-\u{066C}\u{06D4}]/gu;
const EASTERN_DIGIT_RE = /[\u{0660}-\u{0669}\u{06F0}-\u{06F9}]/gu;

const WHITESPACE = /\s+/gu;

function mapDigitOrPunct(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
  if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
  return PUNCTUATION[ch] ?? ch;
}

/** Converts Arabic-Indic (٠-٩) and Persian (۰-۹) digits to ASCII digits; nothing else. */
export function toLatinDigits(input: string): string {
  return input.replace(EASTERN_DIGIT_RE, mapDigitOrPunct);
}

/** Normalizes text to the canonical matching form described above. */
export function normalizeArabic(input: string, options: ArabicNormalizationOptions = {}): string {
  const { foldTehMarbuta = true, foldHamzaCarriers = false, caseFold = true } = options;

  let text = input.normalize("NFKC");
  text = text.replace(INVISIBLE, "").replace(ARABIC_MARKS, "").replace(ARABIC_ZWJ, "");
  text = text.replace(LETTER_FOLD_RE, (ch) => LETTER_FOLDS[ch] ?? ch);
  if (foldTehMarbuta) text = text.replace(TEH_MARBUTA_RE, "\u{0647}");
  if (foldHamzaCarriers) text = text.replace(WAW_HAMZA_RE, "\u{0648}").replace(YEH_HAMZA_RE, YEH);
  text = text.replace(DIGIT_PUNCT_RE, mapDigitOrPunct);
  if (caseFold) text = text.toLowerCase();
  return text.normalize("NFKC").replace(WHITESPACE, " ").trim();
}
